import { faFileInvoice } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import Loader from 'components/Loading/Loader';
import ReportTimeRangePicker from 'components/ReportTimeRangePicker/ReportTimeRangePicker';
import useTimeFilter from 'components/ReportTimeRangePicker/useTimeFilter';
import { currenciesMap } from 'lib/constants/currencies';
import { GetOrganizationData, OrganizationPayoutItem } from 'lib/graphql/entities/organization/types';
import { PayoutStatus, useGetAllOrganizationPayoutsQuery } from 'lib/graphql/graphql';
import { mergePaginatedList, useManualPagination } from 'lib/graphql/pagination';
import useRefetchOnFocus from 'lib/graphql/useRefetchOnFocus';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SmallTitle } from 'styles/styles';

import CreateInvoice from '../CreateInvoice/CreateInvoice';
import type { PayoutStatusOption } from '../PayoutStatusPicker/PayoutStatusPicker';
import PayoutStatusPicker from '../PayoutStatusPicker/PayoutStatusPicker';
import { PayoutCurrency, Payouts } from './styles';

type StatusColors = Record<PayoutStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'>;
const STATUS_COLORS: StatusColors = {
  [PayoutStatus.Canceled]: 'error',
  [PayoutStatus.Failed]: 'error',
  [PayoutStatus.InTransit]: 'info',
  [PayoutStatus.Pending]: 'warning',
  [PayoutStatus.Paid]: 'success',
};

function formatAmount(amount: number, currency: string) {
  const formattedCurrency = currency.toUpperCase();
  const symbol = currenciesMap[formattedCurrency].symbol || '$';
  return `${(amount / 100).toFixed(2)}${symbol} ${formattedCurrency}`;
}

interface PayoutsCardProps {
  currency: string
  organization: GetOrganizationData;
  paidAmount: number
}

function PayoutsCard(props: PayoutsCardProps) {
  const { currency, organization, paidAmount } = props;

  const intl = useIntl();

  const [selectedTime, setSelectedTime] = useState('preset#all');
  const [selectedStatus, setSelectedStatus] = useState<PayoutStatusOption>('all');

  const timeFilter = useTimeFilter(selectedTime);
  const {
    data,
    hasNextPage,
    fetchMore,
    loading,
  } = useRefetchOnFocus(
    useManualPagination(
      useGetAllOrganizationPayoutsQuery({
        variables: {
          id: organization?.id!,
          filter: {
            time: timeFilter,
            status: selectedStatus !== 'all' ? selectedStatus : undefined,
          },
        },
        skip: !organization,
      }),
      (d) => d.getAllOrganizationPayouts.lastEvaluated,
      (existing, incoming) => ({
        getAllOrganizationPayouts: mergePaginatedList(
          existing.getAllOrganizationPayouts,
          incoming.getAllOrganizationPayouts,
          'items',
        ),
      }),
    ),
  );
  const payouts = data?.getAllOrganizationPayouts.items;

  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<OrganizationPayoutItem | null>(null);
  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

  const onPageChange = useCallback(async (model: GridPaginationModel) => {
    const newItemCount = (model.page + 1) * model.pageSize;
    if (payouts && newItemCount > payouts.length) {
      await fetchMore();
    }
    setPaginationModel(model);
  }, [fetchMore, payouts]);

  const openCreateInvoiceDialog = useCallback(async (payout: OrganizationPayoutItem) => {
    setSelectedPayout(payout);
    setInvoiceModalOpen(true);
  }, []);

  const columns: GridColDef<OrganizationPayoutItem>[] = useMemo(() => [
    {
      headerName: intl.formatMessage({ id: 'payout_created_date' }),
      field: 'timestamp',
      flex: 1,
      renderCell: (params) => {
        const date = DateTime.fromISO(params.value).setZone('UTC').startOf('day');
        const diff = Math.ceil(date.diffNow('days').days);
        let relativeTime = '';

        if (diff === -1) {
          relativeTime = intl.formatMessage({ id: 'payout_yesterday' });
        } else if (diff === 0) {
          relativeTime = intl.formatMessage({ id: 'payout_today' });
        }

        return (
          <Tooltip arrow title={relativeTime ? date.toFormat('D') : ''}>
            <span>{relativeTime || date.toFormat('D')}</span>
          </Tooltip>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'payout' }),
      field: 'id',
      flex: 1,
    },
    {
      headerName: intl.formatMessage({ id: 'status' }),
      field: 'status',
      flex: 1,
      renderCell: (params) => {
        const { status } = params.row;
        return (
          <Chip
            label={<FormattedMessage id={`payout_status_${status}`} />}
            color={STATUS_COLORS[status]}
          />
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'payout_arrival_date' }),
      field: 'arrivalDate',
      flex: 2,
      renderCell: (params) => {
        const date = DateTime.fromISO(params.value).setZone('UTC');
        const now = DateTime.now().setZone('UTC');
        const diff = Math.ceil(date.diff(now, 'days').days);
        let relativeTime = '';

        if (diff === -1) {
          relativeTime = intl.formatMessage({ id: 'payout_yesterday' });
        } else if (diff === 0) {
          relativeTime = intl.formatMessage({ id: 'payout_today' });
        } else if (diff === 1) {
          relativeTime = intl.formatMessage({ id: 'payout_tomorrow' });
        } else if (diff > 1) {
          relativeTime = intl.formatMessage({ id: 'payout_in_days' }, { days: diff });
        }

        return (
          <Tooltip arrow title={relativeTime ? date.toFormat('D') : ''}>
            <span>{relativeTime || date.toFormat('D')}</span>
          </Tooltip>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'payout_amount' }),
      field: 'amount',
      flex: 2,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (_, row) => formatAmount(row.amount, currency),
    },
    {
      headerName: intl.formatMessage({ id: 'actions' }),
      field: 'actions',
      headerAlign: 'right',
      align: 'right',
      sortable: false,
      renderCell: (params) => (
        <Tooltip
          title={(
            <FormattedMessage
              id={params.row.status !== PayoutStatus.Paid ? 'create_invoice_disabled' : 'create_invoice'}
            />
          )}
          arrow
        >
          <span>
            <IconButton
              disabled={params.row.status !== PayoutStatus.Paid}
              onClick={() => openCreateInvoiceDialog(params.row)}
            >
              <FontAwesomeIcon icon={faFileInvoice} />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ], [currency, openCreateInvoiceDialog, intl]);

  const visibleRows = useMemo(() => {
    const { page, pageSize } = paginationModel;
    if (!payouts) return [];
    return payouts.slice(page * pageSize, (page + 1) * pageSize);
  }, [paginationModel, payouts]);

  const pickerStartTime = useMemo(() => (
    payouts && payouts.length > 0 ? DateTime.fromISO(payouts[0].timestamp) : DateTime.now()
  ), [payouts]);

  return (
    <Loader isLoading={loading}>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SmallTitle>
                <FormattedMessage id="payouts_to_bank_account" />
              </SmallTitle>
            </Grid>
            <Grid item>
              <ReportTimeRangePicker
                selectedTime={selectedTime}
                onChange={setSelectedTime}
                startTime={pickerStartTime}
              />
            </Grid>
            <Grid item>
              <PayoutStatusPicker
                status={selectedStatus}
                onChange={setSelectedStatus}
              />
            </Grid>
            <Grid item xs />
            <Grid item xs>
              <Payouts>
                <FormattedMessage id="total_payouts" />
                {' '}
                <PayoutCurrency key={currency}>
                  {formatAmount(paidAmount, currency)}
                </PayoutCurrency>
              </Payouts>
            </Grid>
            <Grid item xs={12}>
              <DataGrid
                loading={loading}
                rows={visibleRows}
                rowCount={hasNextPage ? -1 : payouts?.length || 0}
                columns={columns}
                pagination
                paginationModel={paginationModel}
                paginationMode="server"
                onPaginationModelChange={onPageChange}
                pageSizeOptions={[10]}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <CustomDialog
        open={invoiceModalOpen}
        onClose={() => setInvoiceModalOpen(false)}
      >
        <CreateInvoice
          onClose={() => setInvoiceModalOpen(false)}
          payout={selectedPayout}
        />
      </CustomDialog>
    </Loader>
  );
}

export default PayoutsCard;
